<div
  class="igntr-theme min-h-screen min-[992px]:min-h-[calc(100dvh-52px)] text-slate-900 w-full bg-[--backfround-color]"
>
  @if (lockedByPaywall) {
    <div
      class="flex flex-col items-center justify-center text-center min-h-[60vh] px-[16px]"
    >
      <div
        class="max-w-[440px] w-full rounded-2xl border border-slate-200/80 bg-white p-[32px]"
      >
        <div
          class="w-12 h-12 rounded-xl mx-auto flex items-center justify-center bg-[color-mix(in_srgb,var(--primary-color),transparent_88%)] text-[var(--primary-color)]"
        >
          <igntr-icon name="lock" [size]="24"></igntr-icon>
        </div>
        <h2 class="text-[20px] font-semibold text-slate-900 mt-[16px]">
          {{ "subscriptionRequiredTitle" | transloco }}
        </h2>
        <p class="text-[14px] text-slate-500 mt-[8px]">
          {{ "subscriptionRequiredBody" | transloco }}
        </p>
        <button
          [routerLink]="['/billing']"
          class="mt-[20px] inline-flex items-center gap-[6px] px-[18px] py-[10px] rounded-lg bg-[var(--primary-color)] text-white text-[14px] font-medium hover:opacity-90 shadow-[0_6px_18px_-8px_var(--primary-color)]"
        >
          <igntr-icon name="tag" [size]="16"></igntr-icon>
          {{ "goToBilling" | transloco }}
        </button>
      </div>
    </div>
  }
  <!-- pt-7 always; pb-0 on mobile because the bottom navbar (position: fixed)
       overlays the main-container's matching padding-bottom — keeping the
       home page's own pb-7 here would stack on top of it and leave a gray
       strip visible above the navbar. -->
  <div
    class="max-w-[1180px] mx-auto px-[16px] py-[16px] pb-0 md:px-[40px] md:py-[24px] md:pb-[24px] grid grid-cols-1 gap-5"
    [class.hidden]="lockedByPaywall"
  >
    <!-- Wait until the active tenant has been matched before choosing a layout,
         otherwise the default dashboard paints for one tick and blinks before
         the Nybolig grid takes over. -->
    @if (tenantResolved && (!hasQuestionnaire || salesProfileChecked)) {
      @if (hasQuestionnaire && !salesProfileCompleted) {
        <!-- Questionnaire feature: the sales-profile questionnaire drives the
             personal plan, shown on the home page until completed once — then
             the default dashboard takes over (salesProfileCompleted). -->
        <app-dashboard-hero
          [userName]="firstName"
          [minimal]="true"
        ></app-dashboard-hero>
        <app-sales-profile-questionnaire
          [submitting]="salesProfileSaving"
          [initialAnswers]="initialAnswers"
          [initialIndex]="initialIndex"
          (progress)="onSalesProfileProgress($event)"
          (finish)="onSalesProfileFinish($event)"
        ></app-sales-profile-questionnaire>
        @if (showSalesProfileResult && salesProfileScore) {
          <app-sales-profile-result
            [score]="salesProfileScore"
            [plan]="salesProfilePlan"
            [loading]="planLoading"
            (close)="onSalesProfileResultClose()"
          ></app-sales-profile-result>
        }
      } @else if (isNybolig) {
        <!-- Quick-access cards are kept on mobile/tablet (primary navigation
             there) but hidden on desktop (lg+) at the client's request. -->
        <div class="lg:hidden">
          <app-home-menu-grid></app-home-menu-grid>
        </div>
      } @else {
        <!-- For all tenants except Nybolig the tenant "Information" block is
             shown inside the hero card, directly under the greeting/summary
             (projected via ng-content). Nybolig keeps it as a standalone card
             at the bottom (see below). -->
        <app-dashboard-hero
          [userName]="firstName"
          [meetingsCount]="meetingsCount"
          [learningPct]="learningPct"
          [enabledFeatures]="enabledFeatures"
        >
          @if (hasHomeInfo) {
            <ng-container
              [ngTemplateOutlet]="homeInfoBlock"
              [ngTemplateOutletContext]="{ embedded: true }"
            ></ng-container>
          }
        </app-dashboard-hero>

        <!-- Each section is tied to a tenant feature and only rendered when that
             feature is enabled. Column widths collapse to full when the opposite
             column has no visible sections. -->
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <!-- Left column: learning track / toolboxes / news -->
          @if (showLeftColumn) {
            <div
              [class]="
                'grid grid-cols-1 gap-5 ' +
                (showRightColumn ? 'lg:col-span-8' : 'lg:col-span-12')
              "
            >
              @if (hasSprints) {
                <app-dashboard-continue-learning
                  [progress]="learning"
                ></app-dashboard-continue-learning>
              }

              @if (hasToolbox) {
                <app-dashboard-featured-tools
                  [tools]="tools"
                  [loading]="loading"
                ></app-dashboard-featured-tools>
              }

              @if (hasNews) {
                <app-dashboard-news-feed
                  [news]="news"
                  [loading]="loading"
                ></app-dashboard-news-feed>
              }
            </div>
          }

          <!-- Right column: today's calendar / other tasks -->
          @if (showRightColumn) {
            <div
              [class]="
                'grid grid-cols-1 gap-5 content-start ' +
                (showLeftColumn ? 'lg:col-span-4' : 'lg:col-span-12')
              "
            >
              @if (hasCalendar) {
                <app-dashboard-today-agenda
                  [agenda]="agenda"
                  [loading]="loading"
                ></app-dashboard-today-agenda>
              }

              <!-- Hidden until the gamification backend (XP ledger, streaks, badges,
                   weekly missions) is implemented. Currently 100% hardcoded; see
                   dashboard/gamification.component.ts for the TODO. -->
              <!-- <app-dashboard-gamification></app-dashboard-gamification> -->

              @if (hasTodo) {
                <app-dashboard-tasks-summary
                  [tasks]="tasks"
                ></app-dashboard-tasks-summary>
              }
            </div>
          }
        </div>
      }
    }

    <!-- Nybolig keeps the tenant "Information" block below the grid/dashboard.
         Other tenants render it right under the hero (see above). -->
    @if (isNybolig && hasHomeInfo) {
      <ng-container [ngTemplateOutlet]="homeInfoBlock"></ng-container>
    }
  </div>
</div>

<!-- Tenant "Information" tab content (headline / pageHtml / video /
     pageHtml2 / mobile description). Defined once and placed either under the
     hero (non-Nybolig) or below the dashboard (Nybolig). Only rendered when the
     tenant has filled at least one field. -->
<ng-template #homeInfoBlock let-embedded="embedded">
  <!-- Hidden on mobile when there's no mobile-specific content
       (headline / video / Mobile Description) so an empty card isn't
       shown — pageHtml/pageHtml2 are desktop-only.
       `embedded` = rendered inside the hero card: drop the standalone card
       chrome (border / rounded / background) and use the hero's own
       horizontal padding with a separating top border instead. -->
  <section
    [ngClass]="
      embedded
        ? 'px-4 pt-0 pb-1 md:px-7 md:pt-0 -mt-2 md:-mt-3'
        : 'rounded-2xl border border-slate-200/70 bg-white p-6 md:p-8 mb-4 md:mb-0'
    "
    class="md:block"
    [class.hidden]="!hasMobileHomeInfo"
  >
    @if (homeInfo?.headline) {
      <h1
        [ngClass]="embedded ? 'text-xl md:text-2xl mb-2' : 'text-[36px] md:text-[36px] mb-3'"
        class="font-semibold tracking-tight text-slate-900"
      >
        {{ homeInfo?.headline }}
      </h1>
    }
    @if (homeInfo?.logo?.fileUrl) {
      <div class="flex justify-center my-1">
        <img
          [src]="homeInfo?.logo?.fileUrl"
          alt=""
          class="max-h-28 md:max-h-60 w-auto max-w-full object-contain rounded-xl"
        />
      </div>
    }
    @if (homeInfo?.pageHtml) {
      <!-- Desktop-only content. Mobile uses the dedicated "Mobile
           Description" field (descriptionMobile) instead. -->
      <div
        class="hidden md:block text-[14px] leading-relaxed text-slate-600"
        [innerHTML]="homeInfo?.pageHtml"
      ></div>
    }
    @if (videoUrlSafe) {
      <div class="mt-5 w-full max-w-3xl mx-auto aspect-video">
        <iframe
          class="w-full h-full rounded-xl"
          frameBorder="0"
          [src]="videoUrlSafe"
          allowfullscreen
        ></iframe>
      </div>
    }
    @if (homeInfo?.pageHtml2) {
      <div
        class="hidden md:block text-[14px] leading-relaxed text-slate-600 mt-5"
        [innerHTML]="homeInfo?.pageHtml2"
      ></div>
    }
    @if (homeInfo?.descriptionMobile) {
      <div
        class="block md:hidden text-[14px] leading-relaxed text-slate-600 mt-5"
        [innerHTML]="homeInfo?.descriptionMobile"
      ></div>
    }
  </section>
</ng-template>
